import React, { useState, useEffect } from 'react';
import { authAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { FiSave, FiCheck, FiPlus, FiEdit2, FiTrash2, FiX } from 'react-icons/fi';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    apiKey: '',
    pin: '',
    authEndpoint: ''
  });
  const [hasStoredCredentials, setHasStoredCredentials] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Endpoint configurations state
  const [endpointConfigs, setEndpointConfigs] = useState([]);
  const [showEndpointModal, setShowEndpointModal] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [endpointFormData, setEndpointFormData] = useState({
    name: '',
    endpointType: 'SCollect',
    loginUrl: '',
    loginType: 'bearer',
    requestBody: '',
    contentType: 'application/json',
    formParameters: [{ key: '', value: '' }],
    refreshInterval: 1800,
    customVariables: ''
  });
  const [testingEndpoint, setTestingEndpoint] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState(null);

  // Load existing credentials and endpoint configurations when component mounts
  useEffect(() => {
    const loadCredentials = async () => {
      try {
        const response = await authAPI.getProfile();
        const userData = response.data.user;
        
        if (userData.hasApiCredentials) {
          setHasStoredCredentials(true);
          // Show masked values for display
          setFormData({
            apiKey: '••••••••••••••••',
            pin: '••••',
            authEndpoint: '••••••••••••••••••••••••••••••••'
          });
          // Note: We can't retrieve the actual stored values for security reasons
          // The test function will need to handle this case
        }
      } catch (error) {
        console.error('Failed to load user profile:', error);
      }
    };

    const loadEndpointConfigs = async () => {
      try {
        const response = await fetch('/api/dashboard/endpoint-configs', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setEndpointConfigs(data.endpointConfigs);
        }
      } catch (error) {
        console.error('Failed to load endpoint configurations:', error);
      }
    };

    loadCredentials();
    loadEndpointConfigs();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleEndpointChange = (e) => {
    setEndpointFormData({
      ...endpointFormData,
      [e.target.name]: e.target.value
    });
  };

  const showMessage = (text, type = 'success') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const testCredentials = async () => {
    // If we have stored credentials and the form shows masked values, test the stored ones
    if (hasStoredCredentials && formData.apiKey.includes('••••')) {
      setTesting(true);
      try {
        // Call a special endpoint that tests the user's stored credentials
        await authAPI.testStoredCredentials();
        showMessage('Stored credentials are valid!', 'success');
      } catch (error) {
        showMessage(
          error.response?.data?.error || 'Failed to validate stored credentials',
          'error'
        );
      } finally {
        setTesting(false);
      }
      return;
    }

    // Otherwise, test the current form values
    if (!formData.apiKey || !formData.pin || !formData.authEndpoint) {
      showMessage('Please fill in all fields before testing', 'error');
      return;
    }

    setTesting(true);
    try {
      await authAPI.testCredentials(formData.apiKey, formData.pin, formData.authEndpoint);
      showMessage('Credentials are valid!', 'success');
    } catch (error) {
      showMessage(
        error.response?.data?.error || 'Failed to validate credentials',
        'error'
      );
    } finally {
      setTesting(false);
    }
  };

  const saveCredentials = async (e) => {
    e.preventDefault();
    
    if (!formData.apiKey || !formData.pin || !formData.authEndpoint) {
      showMessage('Please fill in all fields', 'error');
      return;
    }

    setLoading(true);
    try {
      await authAPI.updateCredentials(formData.apiKey, formData.pin, formData.authEndpoint);
      updateUser({ hasApiCredentials: true });
      showMessage('API credentials saved successfully!', 'success');
    } catch (error) {
      showMessage(
        error.response?.data?.error || 'Failed to save credentials',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  // Endpoint configuration functions
  const openEndpointModal = (endpoint = null) => {
    if (endpoint) {
      setEditingEndpoint(endpoint);
      // Parse form parameters if they exist
      let formParameters = [{ key: '', value: '' }];
      if (endpoint.formParameters) {
        try {
          formParameters = JSON.parse(endpoint.formParameters);
          // Ensure we have at least one empty parameter for adding new ones
          if (formParameters.length === 0) {
            formParameters = [{ key: '', value: '' }];
          }
        } catch (e) {
          console.error('Error parsing form parameters:', e);
          formParameters = [{ key: '', value: '' }];
        }
      }
      
      setEndpointFormData({
        name: endpoint.name,
        endpointType: endpoint.endpointType,
        loginUrl: endpoint.loginUrl || '',
        loginType: endpoint.loginType || 'bearer',
        requestBody: endpoint.requestBody ? JSON.stringify(endpoint.requestBody, null, 2) : '',
        contentType: endpoint.contentType || 'application/json',
        formParameters: formParameters,
        refreshInterval: endpoint.refreshInterval || 1800,
        customVariables: endpoint.customVariables ? 
          (typeof endpoint.customVariables === 'string' ? endpoint.customVariables : JSON.stringify(endpoint.customVariables, null, 2)) : ''
      });
    } else {
      setEditingEndpoint(null);
      setEndpointFormData({
        name: '',
        endpointType: 'SCollect',
        loginUrl: '',
        loginType: 'bearer',
        requestBody: '',
        contentType: 'application/json',
        formParameters: [{ key: '', value: '' }],
        refreshInterval: 1800,
        customVariables: ''
      });
    }
    // Clear test results when opening modal
    setTestResult(null);
    setTestError(null);
    setTestingEndpoint(false);
    setShowEndpointModal(true);
  };

  const closeEndpointModal = () => {
    setShowEndpointModal(false);
    setEditingEndpoint(null);
    setEndpointFormData({
      name: '',
      endpointType: 'SCollect',
      loginUrl: '',
      loginType: 'bearer',
      requestBody: '',
      contentType: 'application/json',
      formParameters: [{ key: '', value: '' }],
      refreshInterval: 1800,
      customVariables: ''
    });
    // Clear test results when closing modal
    setTestResult(null);
    setTestError(null);
    setTestingEndpoint(false);
  };

  const saveEndpointConfig = async (e) => {
    e.preventDefault();
    
    if (!endpointFormData.name || !endpointFormData.endpointType) {
      showMessage('Please fill in required fields', 'error');
      return;
    }

    try {
      const payload = {
        ...endpointFormData,
        requestBody: endpointFormData.contentType === 'application/json' && endpointFormData.requestBody 
          ? JSON.parse(endpointFormData.requestBody) 
          : null,
        formParameters: endpointFormData.contentType === 'application/x-www-form-urlencoded' 
          ? JSON.stringify(endpointFormData.formParameters.filter(param => param.key.trim() !== ''))
          : null
      };

      const url = editingEndpoint 
        ? `/api/dashboard/endpoint-configs/${editingEndpoint.id}`
        : '/api/dashboard/endpoint-configs';
      
      const method = editingEndpoint ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (editingEndpoint) {
          setEndpointConfigs(endpointConfigs.map(config => 
            config.id === editingEndpoint.id ? data.endpointConfig : config
          ));
          showMessage('Endpoint configuration updated successfully!', 'success');
        } else {
          setEndpointConfigs([...endpointConfigs, data.endpointConfig]);
          showMessage('Endpoint configuration created successfully!', 'success');
        }
        closeEndpointModal();
      } else {
        const errorData = await response.json();
        showMessage(errorData.error || 'Failed to save endpoint configuration', 'error');
      }
    } catch (error) {
      showMessage('Invalid JSON in request body or network error', 'error');
    }
  };

  const deleteEndpointConfig = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this endpoint configuration?')) {
      return;
    }

    try {
      const response = await fetch(`/api/dashboard/endpoint-configs/${configId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setEndpointConfigs(endpointConfigs.filter(config => config.id !== configId));
        showMessage('Endpoint configuration deleted successfully!', 'success');
      } else {
        const errorData = await response.json();
        showMessage(errorData.error || 'Failed to delete endpoint configuration', 'error');
      }
    } catch (error) {
      showMessage('Failed to delete endpoint configuration', 'error');
    }
  };

  const testEndpointConfig = async () => {
    if (!endpointFormData.name || !endpointFormData.endpointType || !endpointFormData.loginUrl) {
      setTestError('Please fill in required fields (name, endpoint type, and login URL) before testing');
      return;
    }

    setTestingEndpoint(true);
    setTestResult(null);
    setTestError(null);

    try {
      const payload = {
        name: endpointFormData.name,
        endpointType: endpointFormData.endpointType,
        loginUrl: endpointFormData.loginUrl,
        loginType: endpointFormData.loginType,
        contentType: endpointFormData.contentType
      };

      // Add appropriate body data based on content type
      if (endpointFormData.contentType === 'application/json' && endpointFormData.requestBody) {
        try {
          payload.requestBody = JSON.parse(endpointFormData.requestBody);
        } catch (error) {
          setTestError('Invalid JSON in request body');
          setTestingEndpoint(false);
          return;
        }
      } else if (endpointFormData.contentType === 'application/x-www-form-urlencoded') {
        payload.formParameters = JSON.stringify(endpointFormData.formParameters.filter(param => param.key.trim() !== ''));
      }

      const response = await fetch('/api/dashboard/test-endpoint-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setTestResult(data);
        setTestError(null);
      } else {
        const errorData = await response.json();
        setTestError(errorData.error || 'Test failed');
        setTestResult(null);
      }
    } catch (error) {
      setTestError('Network error or invalid request data');
      setTestResult(null);
    } finally {
      setTestingEndpoint(false);
    }
  };

  const getEndpointTypeDescription = (type) => {
    switch (type) {
      case 'SCollect':
        return 'ServiceNow Service Catalog Collection API';
      case 'Solutions By Text':
        return 'Solutions By Text API integration';
      default:
        return 'Custom API endpoint';
    }
  };

  const getLoginTypeDescription = (type) => {
    switch (type) {
      case 'bearer':
        return 'Bearer token authentication';
      case 'basic':
        return 'Basic username/password authentication';
      case 'custom':
        return 'Custom authentication method';
      default:
        return 'Authentication method';
    }
  };

  return (
    <div className="container" style={{ maxWidth: '600px' }}>
      <h1>Settings</h1>
      
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">API Credentials</h2>
        </div>
        
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Configure your API credentials to connect to external data sources. 
          These credentials will be used to generate bearer tokens for API requests.
        </p>

        <form onSubmit={saveCredentials}>
          <div className="form-group">
            <label className="form-label">API Key</label>
            <input
              type="text"
              name="apiKey"
              value={formData.apiKey}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your API key"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">PIN</label>
            <input
              type="password"
              name="pin"
              value={formData.pin}
              onChange={handleChange}
              className="form-input"
              placeholder="Enter your PIN"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Authentication Endpoint</label>
            <input
              type="url"
              name="authEndpoint"
              value={formData.authEndpoint}
              onChange={handleChange}
              className="form-input"
              placeholder="https://api.example.com/auth"
              required
            />
            <small style={{ color: '#666', fontSize: '12px' }}>
              The endpoint where your API key and PIN will be sent to generate bearer tokens
            </small>
          </div>

          {message && (
            <div 
              className={messageType === 'error' ? 'error-message' : 'success-message'}
              style={{ marginBottom: '20px' }}
            >
              {message}
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              onClick={testCredentials}
              disabled={testing}
              className="btn btn-secondary"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <FiCheck style={{ marginRight: '5px' }} />
              {testing ? 'Testing...' : 'Test Credentials'}
            </button>

            <button
              type="submit"
              disabled={loading}
              className="btn btn-primary"
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <FiSave style={{ marginRight: '5px' }} />
              {loading ? 'Saving...' : 'Save Credentials'}
            </button>
          </div>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Account Information</h2>
        </div>
        
        <div style={{ display: 'grid', gap: '15px' }}>
          <div>
            <strong>Username:</strong> {user?.username}
          </div>
          <div>
            <strong>Email:</strong> {user?.email}
          </div>
          <div>
            <strong>Role:</strong> {user?.role}
          </div>
          <div>
            <strong>API Credentials:</strong>{' '}
            <span style={{ 
              color: user?.hasApiCredentials ? '#28a745' : '#dc3545',
              fontWeight: '500'
            }}>
              {user?.hasApiCredentials ? 'Configured' : 'Not Configured'}
            </span>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="card-title">Endpoint Configurations</h2>
          <button
            onClick={() => openEndpointModal()}
            className="btn btn-primary"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <FiPlus style={{ marginRight: '5px' }} />
            Add Configuration
          </button>
        </div>
        
        <p style={{ color: '#666', marginBottom: '20px' }}>
          Configure endpoint-specific authentication settings for different API types. 
          These configurations will be used when making requests to specific endpoint types.
        </p>

        {endpointConfigs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>No endpoint configurations found.</p>
            <p>Click "Add Configuration" to create your first endpoint configuration.</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {endpointConfigs.map((config) => (
              <div key={config.id} className="card" style={{ backgroundColor: '#f8f9fa', border: '1px solid #dee2e6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#333' }}>{config.name}</h4>
                    <div style={{ display: 'grid', gap: '8px', fontSize: '14px' }}>
                      <div>
                        <strong>Type:</strong> {config.endpointType}
                        <span style={{ color: '#666', marginLeft: '10px' }}>
                          ({getEndpointTypeDescription(config.endpointType)})
                        </span>
                      </div>
                      {config.loginUrl && (
                        <div>
                          <strong>Login URL:</strong> {config.loginUrl}
                        </div>
                      )}
                      <div>
                        <strong>Auth Type:</strong> {config.loginType}
                        <span style={{ color: '#666', marginLeft: '10px' }}>
                          ({getLoginTypeDescription(config.loginType)})
                        </span>
                      </div>
                      {config.requestBody && (
                        <div>
                          <strong>Has Custom Request Body:</strong> Yes
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => openEndpointModal(config)}
                      className="btn btn-secondary"
                      style={{ padding: '8px 12px' }}
                    >
                      <FiEdit2 />
                    </button>
                    <button
                      onClick={() => deleteEndpointConfig(config.id)}
                      className="btn btn-danger"
                      style={{ padding: '8px 12px' }}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {user?.hasApiCredentials && (
        <div className="card" style={{ backgroundColor: '#d4edda', borderColor: '#c3e6cb' }}>
          <h3 style={{ color: '#155724', marginBottom: '10px' }}>✅ Setup Complete</h3>
          <p style={{ color: '#155724', margin: 0 }}>
            Your API credentials are configured. You can now use the dashboard to view KPI data.
            Visit the <a href="/dashboard" style={{ color: '#155724' }}>Dashboard</a> to get started.
          </p>
        </div>
      )}

      {/* Endpoint Configuration Modal */}
      {showEndpointModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3>{editingEndpoint ? 'Edit Endpoint Configuration' : 'Add Endpoint Configuration'}</h3>
              <button
                onClick={closeEndpointModal}
                style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={saveEndpointConfig}>
              <div className="form-group">
                <label className="form-label">Configuration Name *</label>
                <input
                  type="text"
                  name="name"
                  value={endpointFormData.name}
                  onChange={handleEndpointChange}
                  className="form-input"
                  placeholder="e.g., Production SCollect, Test Environment"
                  required
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  A unique name to identify this configuration
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Endpoint Type *</label>
                <select
                  name="endpointType"
                  value={endpointFormData.endpointType}
                  onChange={handleEndpointChange}
                  className="form-input"
                  required
                >
                  <option value="SCollect">SCollect</option>
                  <option value="Solutions By Text">Solutions By Text</option>
                  <option value="Custom">Custom</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  {getEndpointTypeDescription(endpointFormData.endpointType)}
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Login URL</label>
                <input
                  type="url"
                  name="loginUrl"
                  value={endpointFormData.loginUrl}
                  onChange={handleEndpointChange}
                  className="form-input"
                  placeholder="https://api.example.com/login"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  The authentication endpoint for this API
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Authentication Type</label>
                <select
                  name="loginType"
                  value={endpointFormData.loginType}
                  onChange={handleEndpointChange}
                  className="form-input"
                >
                  <option value="bearer">Bearer Token</option>
                  <option value="basic">Basic Auth (Username/Password)</option>
                  <option value="custom">Custom</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  {getLoginTypeDescription(endpointFormData.loginType)}
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Content Type</label>
                <select
                  name="contentType"
                  value={endpointFormData.contentType}
                  onChange={handleEndpointChange}
                  className="form-input"
                >
                  <option value="application/json">JSON (application/json)</option>
                  <option value="application/x-www-form-urlencoded">Form Data (application/x-www-form-urlencoded)</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px' }}>
                  Choose the content type for the request body
                </small>
              </div>

              {endpointFormData.contentType === 'application/json' ? (
                <div className="form-group">
                  <label className="form-label">Request Body (JSON)</label>
                  <textarea
                    name="requestBody"
                    value={endpointFormData.requestBody}
                    onChange={handleEndpointChange}
                    className="form-input"
                    rows="6"
                    placeholder={'{"username": "{{apiKey}}", "password": "{{pin}}"}'}
                    style={{ fontFamily: 'monospace', fontSize: '14px' }}
                  />
                  <small style={{ color: '#666', fontSize: '12px' }}>
                    JSON body for authentication requests. Use {'{{'} apiKey {'}}'} and {'{{'} pin {'}}'} as placeholders for your credentials.
                  </small>
                </div>
              ) : (
                <div className="form-group">
                  <label className="form-label">Form Parameters</label>
                  {endpointFormData.formParameters.map((param, index) => (
                    <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                      <input
                        type="text"
                        placeholder="Parameter name"
                        value={param.key}
                        onChange={(e) => {
                          const newParams = [...endpointFormData.formParameters];
                          newParams[index].key = e.target.value;
                          setEndpointFormData({ ...endpointFormData, formParameters: newParams });
                        }}
                        className="form-input"
                        style={{ flex: 1 }}
                      />
                      <input
                        type="text"
                        placeholder="Parameter value (use {{apiKey}} or {{pin}})"
                        value={param.value}
                        onChange={(e) => {
                          const newParams = [...endpointFormData.formParameters];
                          newParams[index].value = e.target.value;
                          setEndpointFormData({ ...endpointFormData, formParameters: newParams });
                        }}
                        className="form-input"
                        style={{ flex: 2 }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newParams = endpointFormData.formParameters.filter((_, i) => i !== index);
                          setEndpointFormData({ ...endpointFormData, formParameters: newParams });
                        }}
                        className="btn btn-danger"
                        style={{ padding: '8px 12px' }}
                        disabled={endpointFormData.formParameters.length === 1}
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setEndpointFormData({
                        ...endpointFormData,
                        formParameters: [...endpointFormData.formParameters, { key: '', value: '' }]
                      });
                    }}
                    className="btn btn-secondary"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <FiPlus style={{ marginRight: '5px' }} />
                    Add Parameter
                  </button>
                  <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '10px' }}>
                    Form parameters for x-www-form-urlencoded requests. Use {'{{'} apiKey {'}}'} and {'{{'} pin {'}}'} as placeholders for your credentials.
                  </small>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Token Refresh Interval (seconds)</label>
                <input
                  type="number"
                  name="refreshInterval"
                  value={endpointFormData.refreshInterval}
                  onChange={handleEndpointChange}
                  className="form-input"
                  min="300"
                  max="86400"
                  placeholder="1800"
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  How often tokens should be refreshed automatically (default: 1800 seconds = 30 minutes)
                </small>
              </div>

              <div className="form-group">
                <label className="form-label">Custom Variables</label>
                <textarea
                  name="customVariables"
                  value={endpointFormData.customVariables}
                  onChange={handleEndpointChange}
                  className="form-input"
                  rows="4"
                  placeholder='{"variable1": "value1", "variable2": "value2"}'
                  style={{ fontFamily: 'monospace', fontSize: '14px' }}
                />
                <small style={{ color: '#666', fontSize: '12px' }}>
                  JSON object containing custom variables that can be used in API requests with {'{{'} custom.variable1 {'}}'} syntax.
                </small>
              </div>

              {/* Test Endpoint Section */}
              <div className="form-group" style={{ borderTop: '1px solid #eee', paddingTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h4 style={{ margin: 0, color: '#333' }}>Test Configuration</h4>
                  <button
                    type="button"
                    onClick={testEndpointConfig}
                    disabled={testingEndpoint || !endpointFormData.loginUrl}
                    className="btn btn-info"
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <FiCheck style={{ marginRight: '5px' }} />
                    {testingEndpoint ? 'Testing...' : 'Test Configuration'}
                  </button>
                </div>

                {testResult && (
                  <div style={{ 
                    backgroundColor: '#d4edda', 
                    border: '1px solid #c3e6cb', 
                    borderRadius: '5px', 
                    padding: '15px',
                    marginBottom: '15px'
                  }}>
                    <h5 style={{ color: '#155724', margin: '0 0 10px 0' }}>✅ Test Successful</h5>
                    <div style={{ fontSize: '14px', color: '#155724' }}>
                      <div><strong>Status:</strong> {testResult.responsePreview?.status}</div>
                      <div><strong>Token Type:</strong> {testResult.tokenType}</div>
                      <div><strong>Token Length:</strong> {testResult.responsePreview?.tokenLength} characters</div>
                      <div><strong>Response Keys:</strong> {testResult.responsePreview?.responseKeys?.join(', ')}</div>
                    </div>
                  </div>
                )}

                {testError && (
                  <div style={{ 
                    backgroundColor: '#f8d7da', 
                    border: '1px solid #f5c6cb', 
                    borderRadius: '5px', 
                    padding: '15px',
                    marginBottom: '15px'
                  }}>
                    <h5 style={{ color: '#721c24', margin: '0 0 10px 0' }}>❌ Test Failed</h5>
                    <p style={{ color: '#721c24', margin: 0 }}>{testError}</p>
                  </div>
                )}

                {!testResult && !testError && !testingEndpoint && (
                  <p style={{ color: '#666', fontSize: '14px', margin: 0 }}>
                    Test your endpoint configuration to ensure it works with your API credentials.
                  </p>
                )}
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={closeEndpointModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingEndpoint ? 'Update Configuration' : 'Create Configuration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
